import { ReactNode, Suspense, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { Export } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { Button } from "@/components/ui/button"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { UserProvider } from "@/components/user-wrapper"
import { setDocumentTitle } from "@/lib/environment-title-prefix"
import { useIsChatLocation } from "@/modules/chats/lib/routing-helpers"
import { ButtonContinueInboundCall } from "@/modules/patients/components/button-continue-inbound-call"
import { ConcludeAction } from "@/modules/patients/components/header/ConcludeAction"
import {
  AmbulanceIcon,
  EmergencyServiceDialog,
} from "@/modules/patients/components/header/emergency-service/emergency-service-dialog"
import { ShareMedicalRecord } from "@/modules/patients/components/header/ShareMedicalRecord"
import { PatientInfoForMedicalRecord } from "@/modules/patients/components/patient-info/patient-info-for-medical-record"
import { BillingAttributeFragment$key } from "@/modules/patients/detail/conclude/billing-section/services/__generated__/BillingAttributeFragment.graphql"
import { ConcludeDialog } from "@/modules/patients/detail/conclude/conclude-dialog"
import { RecordExtractDialog } from "@/modules/patients/detail/record-extract/record-extract-dialog"
import { EpisodeOfCareOption } from "@/modules/patients/detail/record-extract/types/record-extract-types"
import { PatientData, usePatientFileContext } from "@/modules/patients/detail/store/usePatientStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { PatientFileLockFragment$key } from "@/modules/patients/services/__generated__/PatientFileLockFragment.graphql"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { PatientFileLockFragment } from "@/modules/patients/services/PatientFileLockFragment"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { useRequestTakeOverPatientMutation } from "@/modules/patients/services/useRequestTakeOverPatientMutation"
import { ConsultActionButtons } from "@/modules/telephony/components/consult-action-buttons"
import { usePatientPhoneNumbersOnCall } from "@/modules/telephony/hooks/usePatientPhoneNumbersOnCall"
import { AppointmentEditStatusIndicator } from "@/modules/waiting-room/components/appointment-edit-status-indicator"

interface PatientHeaderProps {
  children?: ReactNode
  patientData: NonNullable<PatientData>
  isStartEditingDisplayed: boolean
  isEditMode: boolean
  isDuplicate?: boolean
  onCloseMedicalRecord?: () => void
  previousProgressNoteBillingDataRef?: BillingAttributeFragment$key | null
  episodeOfCareOptions: EpisodeOfCareOption[]
}

const PatientHeader = ({
  children,
  patientData,
  isStartEditingDisplayed,
  isEditMode,
  isDuplicate = false,
  onCloseMedicalRecord,
  previousProgressNoteBillingDataRef,
  episodeOfCareOptions,
}: PatientHeaderProps) => {
  const { t } = useTranslation()
  const { userName, isOneOf } = useUser()
  const appointmentId = useAppointmentParam()
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const [isOpenEmergencyServiceDialog, setIsOpenEmergencyServiceDialog] = useState(false)
  const [isRecordExtractOpen, setIsRecordExtractOpen] = useState(false)

  const patient = useFragment<PatientFragment$key>(PatientFragment, patientData)
  const canExportPatientRecord =
    isEditMode && isOneOf(["CustomerServiceCenter", "MedicalSupervisor"]) && patient.__typename === "ch_Patient"

  const [requestTakeOverPatient] = useRequestTakeOverPatientMutation()

  const patientFileLock = useFragment<PatientFileLockFragment$key>(PatientFileLockFragment, patient.patientFileLock)
  const phoneNumbersOnCall = usePatientPhoneNumbersOnCall()
  const isChatLocation = useIsChatLocation()
  const { lockedBy, lockedAt } = patientFileLock

  const setPatientFileInfo = usePatientFileContext((state) => state.setPatientFileInfo)
  const setPatientData = usePatientFileContext((state) => state.setPatientData)

  useEffect(() => {
    if (location.pathname.startsWith("/patients")) {
      setDocumentTitle(`${patient.firstName} ${patient.lastName}'s file`)
    }

    setPatientFileInfo(patient)
    setPatientData(patientData)
  }, [patientData, patient, setPatientData, setPatientFileInfo])

  const handleOpenDialog = (isOpen: boolean) => {
    setIsOpenDialog(isOpen)
  }

  return (
    <>
      <PatientInfoForMedicalRecord
        patient={patient}
        contextAppointmentKey={patientData.contextAppointment}
        wrapperClassName="overflow-x-auto"
        contactChannel={patientData.contactChannel}
        patientId={patientData.patientId}
      />
      <div className="ml-auto shrink-0">
        {children}
        {!isDuplicate && (
          <div className="flex gap-1">
            <ButtonContinueInboundCall />

            {!isChatLocation && (
              <ConsultActionButtons
                country={patient.country}
                patientLanguage={patient.primaryLanguage}
                patientId={patient.patientId}
                phoneNumbers={phoneNumbersOnCall}
              />
            )}

            {patient?.__typename === "ch_Patient" && (
              <Button
                data-testid="emergency-service-button"
                variant="tertiary"
                className="p-2"
                onClick={() => {
                  setIsOpenEmergencyServiceDialog(true)
                }}>
                <AmbulanceIcon isCritical />
              </Button>
            )}
            <ShareMedicalRecord />
            {canExportPatientRecord && (
              <TooltipCustom>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="record-extract-button"
                    variant="circle"
                    className="relative h-10 w-10 p-2"
                    aria-label={t("patient.recordExtract.title")}
                    onClick={() => setIsRecordExtractOpen(true)}>
                    <Export className="text-on-surface-subdued" />
                  </Button>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent side="bottom">{t("patient.recordExtract.title")}</TooltipContent>
                </TooltipPortal>
              </TooltipCustom>
            )}
            {isStartEditingDisplayed ? (
              <Button
                data-testid="take-consultation-btn"
                variant="secondary"
                onClick={() => requestTakeOverPatient({ patientId: patient.id, appointmentId: appointmentId })}>
                {t("scheduling.waitingRoom.startEditing")}
              </Button>
            ) : (
              <div className="flex gap-4">
                {lockedBy && (
                  <div className="text-on-surface-subdued mr-auto flex items-center gap-2 text-xs text-ellipsis">
                    <UserProvider
                      userName={lockedBy}
                      render={(user) => (
                        <UserAvatarTooltip user={user} uniqueIdentifier={patient.displayId.toString()} />
                      )}
                    />
                    <AppointmentEditStatusIndicator lockedAt={lockedAt} className="hidden @xl:block" />
                  </div>
                )}
                {isEditMode && (
                  <ConcludeAction
                    patient={patient}
                    appointmentId={appointmentId}
                    onOpenDialog={handleOpenDialog}
                    onCloseMedicalRecord={onCloseMedicalRecord}
                  />
                )}
                {lockedBy && lockedBy !== userName && (
                  <Button
                    className="px-4 py-2"
                    data-testid="take-over-btn"
                    variant="secondary"
                    onClick={() => requestTakeOverPatient({ patientId: patient.id, appointmentId: appointmentId })}>
                    {t("patient.takeOver.requestTakeover")}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {isOpenDialog && (
        <Suspense fallback={null}>
          <ConcludeDialog
            onOpenChange={handleOpenDialog}
            previousProgressNoteBillingDataRef={previousProgressNoteBillingDataRef}
          />
        </Suspense>
      )}
      {isOpenEmergencyServiceDialog && (
        <Suspense fallback={null}>
          <EmergencyServiceDialog onOpenChange={setIsOpenEmergencyServiceDialog} />
        </Suspense>
      )}
      {canExportPatientRecord && isRecordExtractOpen && (
        <RecordExtractDialog
          patient={patient}
          episodeOfCareOptions={episodeOfCareOptions}
          onClose={() => setIsRecordExtractOpen(false)}
        />
      )}
    </>
  )
}

export { PatientHeader }
